"use client";

import {User} from "@supabase/supabase-js";
import {createContext, useContext, useEffect, useMemo, useState} from "react";
import {createClient} from "~/utils/supabase/client";
import {useQuery} from "@tanstack/react-query";
import {AccountResponse} from "@docid/api/src/types/accounts";
import {useClientAuth} from "~/components/providers/client/AuthProvider";
import {fetchById} from "@docid/api/src/models/accounts";

export const dynamic = "force-dynamic";

interface ClientAccountContextProps {
    readonly account: AccountResponse | undefined;
    readonly isAccountLoading: boolean;
    readonly isAccountFetching: boolean;
    readonly invalidateAccount: () => Promise<void>,
}

const ClientAccountContext = createContext<ClientAccountContextProps>({
    account: undefined,
    isAccountLoading: false,
    isAccountFetching: false,
    invalidateAccount: async() => undefined
});

export const ClientAccountProvider = ({ children } : { children: React.ReactNode }) => {

    const { isLoggedIn, isUserReady, user } = useClientAuth();

    const [account, setAccount] = useState<AccountResponse | undefined>();

    const supabase = createClient();

    const fetchAccount = async (user: User): Promise<AccountResponse> => {
        const response = await fetchById({ id: user.id }, supabase as any);
        return response.data as AccountResponse;
    };

    const {
        data,
        error,
        isLoading: isAccountLoading,
        isFetching: isAccountFetching,
        refetch,
    } = useQuery<AccountResponse, Error>({
        queryKey: ["accounts.user"],
        queryFn: () => fetchAccount(user!),
        enabled: Boolean(isLoggedIn && isUserReady && user),
        staleTime: 1000 * 60 * 5, // optional, cache time
    });

    useEffect(() => {
        if (data) {
            setAccount(data);
        }
    }, [data]);

    useEffect(() => {
        if (error) {
            console.error("Failed to fetch user account:", error.message);
        }
    }, [error]);

    const invalidateAccount = async() => {
        await refetch();
    };

    const value = useMemo(() => {
        return {
            account,
            isAccountLoading,
            isAccountFetching,
            invalidateAccount,
        };
    }, [account, isAccountLoading, isAccountFetching, invalidateAccount]);

    return (
        <ClientAccountContext.Provider value={value}>
            {children}
        </ClientAccountContext.Provider>
    );

}

export const useClientAccount = () => {
    const context = useContext(ClientAccountContext);
    if (context === undefined) {
        throw new Error('useClientAccount must be used within an ClientAccountProvider.');
    }
    return context;
};